/**
 * AtlasForge Financial — i18n configuration.
 *
 * Six locales ship at launch: English (default fallback), Spanish, French,
 * German, Portuguese, Japanese. Detection order:
 *   1. `/es/…` `/fr/…` URL prefix (parsed by <LangRouter />).
 *   2. `localStorage.atlasforge_lang`.
 *   3. `atlasforge_lang` cookie.
 *   4. `navigator.language` (browser locale).
 *   5. Fall back to English.
 *
 * Translations live under /src/i18n/locales/<lang>/<namespace>.json and are
 * bundled at build time (webpack tree-shakes unused namespaces per route).
 * Blog posts + developer docs stay English-only for launch — their
 * namespaces exist as empty placeholders so we can drop translations in
 * without a code change.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enCommon from "./locales/en/common.json";
import enHome from "./locales/en/home.json";
import enProducts from "./locales/en/products.json";
import enPages from "./locales/en/pages.json";
import enLegal from "./locales/en/legal.json";
import enPortal from "./locales/en/portal.json";
import enChat from "./locales/en/chat.json";

import esCommon from "./locales/es/common.json";
import esHome from "./locales/es/home.json";
import esProducts from "./locales/es/products.json";
import esPages from "./locales/es/pages.json";
import esLegal from "./locales/es/legal.json";
import esPortal from "./locales/es/portal.json";
import esChat from "./locales/es/chat.json";

import frCommon from "./locales/fr/common.json";
import frHome from "./locales/fr/home.json";
import frProducts from "./locales/fr/products.json";
import frPages from "./locales/fr/pages.json";
import frLegal from "./locales/fr/legal.json";
import frPortal from "./locales/fr/portal.json";
import frChat from "./locales/fr/chat.json";

import deCommon from "./locales/de/common.json";
import deHome from "./locales/de/home.json";
import deProducts from "./locales/de/products.json";
import dePages from "./locales/de/pages.json";
import deLegal from "./locales/de/legal.json";
import dePortal from "./locales/de/portal.json";
import deChat from "./locales/de/chat.json";

import ptCommon from "./locales/pt/common.json";
import ptHome from "./locales/pt/home.json";
import ptProducts from "./locales/pt/products.json";
import ptPages from "./locales/pt/pages.json";
import ptLegal from "./locales/pt/legal.json";
import ptPortal from "./locales/pt/portal.json";
import ptChat from "./locales/pt/chat.json";

import jaCommon from "./locales/ja/common.json";
import jaHome from "./locales/ja/home.json";
import jaProducts from "./locales/ja/products.json";
import jaPages from "./locales/ja/pages.json";
import jaLegal from "./locales/ja/legal.json";
import jaPortal from "./locales/ja/portal.json";
import jaChat from "./locales/ja/chat.json";

// Publicly visible list of supported languages.
// Order controls the language-switcher dropdown rendering.
export const SUPPORTED_LANGS = [
  { code: "en", label: "English", native: "English" },
  { code: "es", label: "Spanish", native: "Español" },
  { code: "fr", label: "French", native: "Français" },
  { code: "de", label: "German", native: "Deutsch" },
  { code: "pt", label: "Portuguese", native: "Português" },
  { code: "ja", label: "Japanese", native: "日本語" },
];

export const SUPPORTED_LANG_CODES = SUPPORTED_LANGS.map((l) => l.code);
export const DEFAULT_LANG = "en";
export const LANG_STORAGE_KEY = "atlasforge_lang";

const resources = {
  en: { common: enCommon, home: enHome, products: enProducts, pages: enPages, legal: enLegal, portal: enPortal, chat: enChat },
  es: { common: esCommon, home: esHome, products: esProducts, pages: esPages, legal: esLegal, portal: esPortal, chat: esChat },
  fr: { common: frCommon, home: frHome, products: frProducts, pages: frPages, legal: frLegal, portal: frPortal, chat: frChat },
  de: { common: deCommon, home: deHome, products: deProducts, pages: dePages, legal: deLegal, portal: dePortal, chat: deChat },
  pt: { common: ptCommon, home: ptHome, products: ptProducts, pages: ptPages, legal: ptLegal, portal: ptPortal, chat: ptChat },
  ja: { common: jaCommon, home: jaHome, products: jaProducts, pages: jaPages, legal: jaLegal, portal: jaPortal, chat: jaChat },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANG,
    supportedLngs: SUPPORTED_LANG_CODES,
    defaultNS: "common",
    ns: ["common", "home", "products", "pages", "legal", "portal", "chat"],
    // Set explicitly by <LangRouter /> from the URL, so detection here is a
    // no-op for first-visit / non-prefixed pages. Order: URL param → storage
    // → cookie → browser locale.
    detection: {
      order: ["localStorage", "cookie", "navigator"],
      lookupLocalStorage: LANG_STORAGE_KEY,
      lookupCookie: LANG_STORAGE_KEY,
      caches: ["localStorage", "cookie"],
      cookieMinutes: 60 * 24 * 365, // 1 year
    },
    interpolation: {
      // React already escapes; disabling avoids double-escape on brand names.
      escapeValue: false,
    },
    react: {
      // Suspense off so lazy-loaded pages don't get stuck if a namespace is
      // still being fetched — we bundle everything statically anyway.
      useSuspense: false,
    },
  });

export default i18n;
